import { useMutation, useQuery } from '@tanstack/react-query';
import { CircleCheck } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { useSearchParams } from 'react-router';
import { Logo } from '../components/Layout';
import { Alert, Button, Card, Spinner } from '../components/ui';
import { api, errorMessage } from '../lib/api';
import { useAuth } from '../lib/auth';
import type { BlackbaudLinkRequestSummary } from '../lib/types';

/** Opened from the Edvora tile or page in Blackbaud, to link a Blackbaud user to the Edvora account signed in here. */
export function BlackbaudLinkPage() {
  const { me, logout } = useAuth();
  const [params] = useSearchParams();
  const code = params.get('code') ?? '';
  const [linked, setLinked] = useState(false);
  const summary = useQuery({
    queryKey: ['blackbaud-link', code],
    queryFn: () => api.get<BlackbaudLinkRequestSummary>(`/blackbaud-links/${encodeURIComponent(code)}`),
    enabled: code.length >= 16 && !!me?.tenant,
    retry: false,
  });
  const confirm = useMutation({
    mutationFn: () => api.post(`/blackbaud-links/${encodeURIComponent(code)}/confirm`),
    onSuccess: () => setLinked(true),
  });
  // Signing out sends this pop-up to sign-in, which comes back here afterwards.
  const switchAccount = (
    <Button variant="ghost" onClick={() => void logout()}>
      Use a different Edvora account
    </Button>
  );

  let body: ReactNode;
  if (!me?.tenant) {
    body = (
      <>
        <Alert tone="amber" title="You don’t have access to this school">
          {me?.user.email} isn’t an Edvora account at this school. Sign in with your school account to link it.
        </Alert>
        {switchAccount}
      </>
    );
  } else if (code.length < 16 || summary.error) {
    body = (
      <Alert tone="red" title="This link doesn’t work">
        {summary.error ? errorMessage(summary.error) : 'It’s missing part of its address. Start again from Edvora in Blackbaud.'}
      </Alert>
    );
  } else if (summary.isLoading || !summary.data) {
    body = <Spinner />;
  } else if (linked) {
    body = (
      <>
        <Alert tone="green" icon={<CircleCheck />} title="Your accounts are linked">
          Go back to Blackbaud. Edvora updates there within a few seconds, and you can close this window.
        </Alert>
        <Button onClick={() => window.close()}>Close window</Button>
      </>
    );
  } else if (summary.data.state !== 'READY') {
    body = (
      <Alert tone="amber" title={summary.data.state === 'USED' ? 'This link was already used' : 'This link expired'}>
        Links work once, for 10 minutes. Start again from Edvora in Blackbaud.
      </Alert>
    );
  } else if (!summary.data.canSeeDevices) {
    body = (
      <>
        <Alert tone="amber" title="Your Edvora account can’t see devices">
          Edvora in Blackbaud shows devices to Edvora viewers, technicians, and administrators. Ask an Edvora administrator for access.
        </Alert>
        {switchAccount}
      </>
    );
  } else {
    const request = summary.data;
    body = (
      <>
        <p className="text-sm text-slate-600">Link these accounts so Edvora in Blackbaud knows who you are.</p>
        <dl className="divide-y divide-slate-100 rounded-lg border border-slate-200 text-sm">
          <div className="px-4 py-3">
            <dt className="text-xs font-medium text-slate-500">Blackbaud account</dt>
            <dd className="font-medium text-slate-900">{request.blackbaudName ?? 'Blackbaud user'}</dd>
            {request.blackbaudEmail && <dd className="text-slate-600">{request.blackbaudEmail}</dd>}
          </div>
          <div className="px-4 py-3">
            <dt className="text-xs font-medium text-slate-500">Edvora account at {me.tenant.name}</dt>
            <dd className="font-medium text-slate-900">{me.user.name}</dd>
            <dd className="text-slate-600">{me.user.email}</dd>
          </div>
        </dl>
        {request.linkedTo === 'SOMEONE_ELSE' && (
          <Alert tone="amber">This Blackbaud account is linked to a different Edvora account. Linking moves it to yours.</Alert>
        )}
        {request.linkedTo === 'YOU' && <Alert tone="blue">It’s already linked to you. Linking again does no harm.</Alert>}
        <Alert tone="blue">
          Only continue if you just asked to link your account from Edvora in Blackbaud. Whoever uses that Blackbaud account uses Edvora
          there as you.
        </Alert>
        {confirm.error && <Alert tone="red">{errorMessage(confirm.error)}</Alert>}
        <div className="flex flex-wrap gap-2">
          <Button variant="primary" loading={confirm.isPending} onClick={() => confirm.mutate()}>
            Link my accounts
          </Button>
          {switchAccount}
        </div>
      </>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-50 px-4 py-10">
      <Card className="w-full max-w-md space-y-4 p-6">
        <div className="flex items-center gap-3">
          <Logo />
          <div>
            <h1 className="text-lg font-semibold text-slate-900">Link your Blackbaud account</h1>
            <p className="text-xs text-slate-500">Edvora Campus Asset</p>
          </div>
        </div>
        {body}
      </Card>
    </div>
  );
}
